import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle, Circle, Clock, XCircle, ArrowRight, CalendarDays,
  ChevronDown, ChevronUp, FileText, Shield, Users, Building2, AlertTriangle,
  Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  borrower: { label: "Borrower", color: "emerald", icon: Users, bg: "bg-emerald-50", border: "border-emerald-200", accent: "text-emerald-700", ring: "ring-emerald-500" },
  lender:   { label: "Lender",   color: "blue",    icon: Building2, bg: "bg-blue-50",    border: "border-blue-200",    accent: "text-blue-700",    ring: "ring-blue-500" },
  admin:    { label: "Admin",    color: "amber",   icon: Shield,    bg: "bg-amber-50",   border: "border-amber-200",   accent: "text-amber-700",   ring: "ring-amber-500" },
};

const STATUS_CONFIG = {
  complete:    { icon: CheckCircle, cls: "text-emerald-600", bg: "bg-emerald-50" },
  waived:      { icon: XCircle,     cls: "text-gray-400",    bg: "bg-gray-50" },
  in_progress: { icon: Clock,       cls: "text-blue-500",    bg: "bg-blue-50" },
  pending:     { icon: Circle,      cls: "text-gray-300",    bg: "bg-white" },
};

function CarriedOverBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-teal-100 text-teal-700 border border-teal-200" data-testid="carried-over-badge">
      <ArrowRight className="h-2.5 w-2.5" />
      Carried Over
    </span>
  );
}

function ProgressRing({ pct, size = 56, strokeWidth = 4, color = "#059669" }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f0ece4" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" style={{ transition: "stroke-dasharray 0.6s ease" }} />
    </svg>
  );
}

function RoleColumn({ role, items, progress, isAdmin, onComplete, onWaive, completing }) {
  const cfg = ROLE_CONFIG[role];
  const Icon = cfg.icon;
  const [expanded, setExpanded] = useState(true);
  const done = progress?.[`${role}_done`] || 0;
  const total = progress?.[`${role}_total`] || 0;
  const pct = progress?.[`${role}_pct`] || 0;

  return (
    <div className={`rounded-xl border ${cfg.border} overflow-hidden`} data-testid={`checklist-column-${role}`}>
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-3 ${cfg.bg} transition-colors hover:brightness-95`}
        data-testid={`checklist-header-${role}`}>
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${cfg.accent}`} />
          <span className={`text-sm font-bold ${cfg.accent}`}>{cfg.label}</span>
          <span className="text-xs text-gray-500">{done}/{total}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-20 h-1.5 rounded-full bg-gray-200 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: pct === 100 ? "#059669" : pct > 50 ? "#0d9488" : "#94a3b8" }} />
          </div>
          <span className="text-[10px] font-mono font-bold text-gray-500">{pct}%</span>
          {expanded ? <ChevronUp className="h-3 w-3 text-gray-400" /> : <ChevronDown className="h-3 w-3 text-gray-400" />}
        </div>
      </button>

      {/* Items */}
      {expanded && (
        <div className="divide-y divide-gray-100">
          {items.map(item => (
            <ChecklistItem key={item.item_id} item={item} isAdmin={isAdmin} onComplete={onComplete} onWaive={onWaive} completing={completing} />
          ))}
          {items.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-gray-400">No items</div>
          )}
        </div>
      )}
    </div>
  );
}

function ChecklistItem({ item, isAdmin, onComplete, onWaive, completing }) {
  const [showActions, setShowActions] = useState(false);
  const [waiveReason, setWaiveReason] = useState("");
  const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusCfg.icon;
  const isCompleting = completing === item.item_id;
  const isDone = item.status === "complete" || item.status === "waived";

  return (
    <div className={`px-4 py-3 ${statusCfg.bg} transition-colors hover:bg-gray-50/50`} data-testid={`checklist-item-${item.item_id}`}>
      <div className="flex items-start gap-3">
        {/* Status icon */}
        <div className="mt-0.5 flex-shrink-0">
          {isCompleting ? (
            <Loader2 className="h-4 w-4 text-emerald-500 animate-spin" />
          ) : (
            <StatusIcon className={`h-4 w-4 ${statusCfg.cls}`} />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${isDone ? "text-gray-500 line-through" : "text-[#0A0A0A]"}`}>{item.title}</span>
            {item.is_carry_over && item.status === "complete" && <CarriedOverBadge />}
            {item.status === "waived" && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-500 border border-gray-200">
                Waived
              </span>
            )}
          </div>
          {item.description && !isDone && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
          {item.notes && isDone && <p className="text-[11px] text-gray-400 mt-0.5 italic">{item.notes}</p>}
          {item.status === "waived" && item.waived_reason && (
            <p className="text-[11px] text-gray-400 mt-0.5">Reason: {item.waived_reason}</p>
          )}
        </div>

        {/* Actions */}
        {!isDone && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={() => onComplete(item.item_id)}
              disabled={isCompleting}
              className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50"
              data-testid={`complete-item-${item.item_id}`}
            >
              Complete
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowActions(!showActions)}
                className="px-2 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                data-testid={`waive-toggle-${item.item_id}`}
              >
                Waive
              </button>
            )}
          </div>
        )}
      </div>

      {/* Waive form */}
      {showActions && !isDone && isAdmin && (
        <div className="mt-2 ml-7 flex items-center gap-2" data-testid={`waive-form-${item.item_id}`}>
          <input
            value={waiveReason}
            onChange={e => setWaiveReason(e.target.value)}
            placeholder="Reason for waiving..."
            className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
          />
          <button
            onClick={() => { if (waiveReason.trim()) { onWaive(item.item_id, waiveReason); setShowActions(false); setWaiveReason(""); } }}
            disabled={!waiveReason.trim()}
            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            Confirm Waive
          </button>
        </div>
      )}
    </div>
  );
}


export default function ClosingChecklist({ leadId, token, userRole, dealNumber, onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [targetDate, setTargetDate] = useState("");
  const [editingDate, setEditingDate] = useState(false);

  const isAdmin = userRole === "admin";

  const fetchChecklist = useCallback(async () => {
    try {
      const [checklistRes, dateRes] = await Promise.all([
        fetch(`${API}/api/funding/deal/${leadId}/checklist`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/funding/deal/${leadId}/target-close-date`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const checklist = await checklistRes.json();
      const dateData = await dateRes.json();
      setData(checklist);
      setTargetDate(dateData.target_close_date || "");
    } catch (e) {
      console.error("Failed to load checklist", e);
    } finally {
      setLoading(false);
    }
  }, [leadId, token]);

  useEffect(() => { fetchChecklist(); }, [fetchChecklist]);

  const handleComplete = async (itemId) => {
    setCompleting(itemId);
    try {
      await fetch(`${API}/api/funding/checklist/${itemId}/complete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      fetchChecklist();
      onRefresh?.();
    } finally {
      setCompleting(null);
    }
  };

  const handleWaive = async (itemId, reason) => {
    setCompleting(itemId);
    try {
      await fetch(`${API}/api/funding/checklist/${itemId}/waive`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ reason }),
      });
      fetchChecklist();
      onRefresh?.();
    } finally {
      setCompleting(null);
    }
  };

  const handleSetDate = async () => {
    if (!targetDate) return;
    await fetch(`${API}/api/funding/deal/${leadId}/target-close-date`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ target_date: targetDate }),
    });
    setEditingDate(false);
    fetchChecklist();
  };

  const generateChecklist = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/api/funding/deal/${leadId}/checklist/generate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchChecklist();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="checklist-loading">
        <Loader2 className="h-6 w-6 text-emerald-600 animate-spin" />
        <span className="ml-2 text-sm text-gray-500">Loading closing checklist...</span>
      </div>
    );
  }

  const progress = data?.progress;
  const hasItems = data?.items?.length > 0;

  // No checklist yet — show generate button (admin only)
  if (!hasItems) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 p-8 text-center" data-testid="no-checklist">
        <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <h3 className="text-sm font-semibold text-gray-700 mb-1">No Closing Checklist Yet</h3>
        <p className="text-xs text-gray-500 mb-4">Generate the closing checklist to start tracking the funding process.</p>
        {isAdmin && (
          <button onClick={generateChecklist} className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors" data-testid="generate-checklist-btn">
            Generate Closing Checklist
          </button>
        )}
      </div>
    );
  }

  // Calculate days until target close
  let daysUntilClose = null;
  if (targetDate) {
    const now = new Date();
    const target = new Date(targetDate);
    daysUntilClose = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  }

  return (
    <div className="space-y-4" data-testid="closing-checklist">
      {/* ── Overall Progress Header ── */}
      <div className="rounded-xl border border-[#EDE8DC] bg-white p-4" data-testid="checklist-progress">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <ProgressRing pct={progress.pct} color={progress.all_complete ? "#059669" : "#0d9488"} />
              <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-[#0A0A0A]">{progress.pct}%</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-[#0A0A0A]">Closing Progress</h3>
              <p className="text-xs text-gray-500">{progress.done} of {progress.total} items complete &middot; {progress.carry_over_count} carried over from intake</p>
            </div>
          </div>

          {/* Target close date */}
          <div className="flex items-center gap-2">
            {daysUntilClose !== null && (
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                daysUntilClose <= 0 ? "bg-red-50 border-red-200 text-red-700" :
                daysUntilClose <= 7 ? "bg-amber-50 border-amber-200 text-amber-700" :
                "bg-emerald-50 border-emerald-200 text-emerald-700"
              }`} data-testid="days-until-close">
                <CalendarDays className="h-3.5 w-3.5" />
                {daysUntilClose <= 0 ? "Overdue" : `${daysUntilClose} day${daysUntilClose !== 1 ? "s" : ""} to close`}
              </div>
            )}
            {isAdmin && !editingDate && (
              <button onClick={() => setEditingDate(true)}
                className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                data-testid="set-close-date-btn">
                <CalendarDays className="h-3 w-3 inline mr-1" />
                {targetDate ? "Change Date" : "Set Close Date"}
              </button>
            )}
            {editingDate && (
              <div className="flex items-center gap-1.5">
                <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)}
                  className="px-2 py-1 border border-gray-200 rounded-lg text-xs" data-testid="close-date-input" />
                <button onClick={handleSetDate} className="px-2 py-1 rounded-lg text-[11px] font-semibold bg-emerald-600 text-white hover:bg-emerald-700" data-testid="save-close-date-btn">Save</button>
                <button onClick={() => setEditingDate(false)} className="px-2 py-1 rounded-lg text-[11px] font-semibold bg-gray-100 text-gray-500 hover:bg-gray-200">Cancel</button>
              </div>
            )}
          </div>
        </div>

        {/* Ready to Fund banner */}
        {progress.all_complete && (
          <div className="mt-3 p-3 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 flex items-center gap-2" data-testid="ready-to-fund-banner">
            <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-800">Ready to Fund</p>
              <p className="text-xs text-emerald-600">All closing checklist items are complete. This deal can be moved to funded status.</p>
            </div>
          </div>
        )}

        {/* Overdue warning */}
        {daysUntilClose !== null && daysUntilClose <= 0 && !progress.all_complete && (
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 flex items-center gap-2" data-testid="overdue-warning">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-red-800">Closing Date Passed</p>
              <p className="text-xs text-red-600">The target closing date has passed with {progress.pending} item{progress.pending !== 1 ? "s" : ""} still pending.</p>
            </div>
          </div>
        )}
      </div>

      {/* ── 3-Column Checklist ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="checklist-columns">
        <RoleColumn role="borrower" items={data.borrower} progress={progress} isAdmin={isAdmin} onComplete={handleComplete} onWaive={handleWaive} completing={completing} />
        <RoleColumn role="lender" items={data.lender} progress={progress} isAdmin={isAdmin} onComplete={handleComplete} onWaive={handleWaive} completing={completing} />
        <RoleColumn role="admin" items={data.admin} progress={progress} isAdmin={isAdmin} onComplete={handleComplete} onWaive={handleWaive} completing={completing} />
      </div>
    </div>
  );
}
